// ========================================
// SophyGames - Página Principal (Modo Estudo)
// ========================================
// Versão: 1.0
// Data: 01 de Novembro de 2025

import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../services/api';
import Flashcard from '../components/Flashcard';

interface Termo {
  id: number;
  termo: string;
  significado: string;
  descricao?: string;
  tema: string;
  area_estudo: string;
  dificuldade: number;
}

interface TemasAreas {
  temas: string[];
  areas: string[];
}

export default function SophyGames() {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Estados
  const [temasAreas, setTemasAreas] = useState<TemasAreas>({ temas: [], areas: [] });
  const [termos, setTermos] = useState<Termo[]>([]);
  const [termoAtual, setTermoAtual] = useState<Termo | null>(null);
  const [indiceAtual, setIndiceAtual] = useState(0);
  const [loading, setLoading] = useState(false);
  
  // Filtros
  const [tema, setTema] = useState('');
  const [areaEstudo, setAreaEstudo] = useState('');
  const [dificuldadeMin, setDificuldadeMin] = useState(1);
  const [dificuldadeMax, setDificuldadeMax] = useState(10);
  const [totalCartas, setTotalCartas] = useState(10);
  const [modo, setModo] = useState<'estudo' | 'competicao'>('estudo');
  
  // Estado de jogo
  const [jogoIniciado, setJogoIniciado] = useState(false);
  
  // Estados para modo competição
  const [partidaId, setPartidaId] = useState<number | null>(null);
  const [respostaUsuario, setRespostaUsuario] = useState('');
  const [timer, setTimer] = useState(0);
  const [respostas, setRespostas] = useState<any[]>([]);
  const [mostrarDescricao, setMostrarDescricao] = useState(false);
  const [resultadoResposta, setResultadoResposta] = useState<{
    correta: boolean;
    significadoCorreto: string;
  } | null>(null);

  // Carregar temas e áreas ao montar
  useEffect(() => {
    carregarTemasAreas();
  }, []);

  // Timer para modo competição
  useEffect(() => {
    if (jogoIniciado && modo === 'competicao') {
      const interval = setInterval(() => {
        setTimer(prev => prev + 1);
      }, 1000);
      
      return () => clearInterval(interval);
    }
  }, [jogoIniciado, modo]);

  const carregarTemasAreas = async () => {
    try {
      const response = await api.get('/sophygames/temas-areas');
      
      if (response.data.sucesso) {
        setTemasAreas(response.data.dados);
      } else {
        alert('Erro ao carregar temas e áreas: ' + response.data.mensagem);
      }
    } catch (error: any) {
      console.error('[SOPHYGAMES] Erro ao carregar temas/áreas:', error);
      alert('Erro ao carregar temas e áreas disponíveis');
    }
  };

  const iniciarJogo = async () => {
    setLoading(true);
    
    try {
      const response = await api.post('/sophygames/partida/iniciar', {
        tipoJogo: 'flashcards',
        modo,
        tema: tema || undefined,
        area_estudo: areaEstudo || undefined,
        dificuldadeMin,
        dificuldadeMax,
        totalCartas
      });
      
      if (response.data.sucesso) {
        const { termos: termosCarregados, partidaId: id } = response.data.dados;
        setTermos(termosCarregados);
        setTermoAtual(termosCarregados[0]);
        setIndiceAtual(0);
        setJogoIniciado(true);
        setPartidaId(id);
        setTimer(0);
        setRespostas([]);
        
        console.log('[SOPHYGAMES] Jogo iniciado:', {
          modo,
          totalTermos: termosCarregados.length,
          partidaId: id
        });
      } else {
        alert('Erro ao iniciar jogo: ' + response.data.mensagem);
      }
    } catch (error: any) {
      console.error('[SOPHYGAMES] Erro ao iniciar jogo:', error);
      alert('Erro ao iniciar jogo: ' + (error.response?.data?.mensagem || error.message));
    } finally {
      setLoading(false);
    }
  };

  const proximoCarta = () => {
    const proximoIndice = indiceAtual + 1;
    
    if (proximoIndice < termos.length) {
      setIndiceAtual(proximoIndice);
      setTermoAtual(termos[proximoIndice]);
    } else {
      // Fim do jogo
      alert('🎉 Parabéns! Você completou todos os flashcards!');
      setJogoIniciado(false);
      setTermos([]);
      setTermoAtual(null);
      setIndiceAtual(0);
    }
  };

  const cartaAnterior = () => {
    const anteriorIndice = indiceAtual - 1;
    
    if (anteriorIndice >= 0) {
      setIndiceAtual(anteriorIndice);
      setTermoAtual(termos[anteriorIndice]);
    }
  };

  const reiniciarJogo = () => {
    setJogoIniciado(false);
    setTermos([]);
    setTermoAtual(null);
    setIndiceAtual(0);
    setPartidaId(null);
    setRespostaUsuario('');
    setTimer(0);
    setRespostas([]);
    setResultadoResposta(null);
  };

  // Função para enviar resposta no modo competição
  const enviarResposta = async () => {
    if (!partidaId || !termoAtual || !respostaUsuario.trim()) {
      alert('Por favor, digite uma resposta');
      return;
    }

    setLoading(true);

    try {
      const response = await api.post('/sophygames/partida/responder', {
        partidaId,
        termoId: termoAtual.id,
        respostaUsuario: respostaUsuario.trim(),
        tempoRespostaSegundos: timer,
        ordemNaPartida: indiceAtual + 1
      });

      if (response.data.sucesso) {
        const { correta, significadoCorreto } = response.data.dados;
        
        setResultadoResposta({ correta, significadoCorreto });
        setRespostas(prev => [...prev, {
          termoId: termoAtual.id,
          termo: termoAtual.termo,
          respostaUsuario: respostaUsuario.trim(),
          correta,
          tempo: timer
        }]);

        // Aguardar 3 segundos mostrando o resultado antes de avançar
        setTimeout(() => {
          setResultadoResposta(null);
          setRespostaUsuario('');
          proximoCartaCompeticao();
        }, 3000);
      } else {
        alert('Erro ao registrar resposta: ' + response.data.mensagem);
      }
    } catch (error: any) {
      console.error('[SOPHYGAMES] Erro ao enviar resposta:', error);
      alert('Erro ao enviar resposta');
    } finally {
      setLoading(false);
    }
  };

  // Próximo carta no modo competição
  const proximoCartaCompeticao = () => {
    const proximoIndice = indiceAtual + 1;
    
    if (proximoIndice < termos.length) {
      setIndiceAtual(proximoIndice);
      setTermoAtual(termos[proximoIndice]);
      setTimer(0); // Reset timer para próxima carta
    } else {
      // Fim do jogo - finalizar partida
      finalizarPartida();
    }
  };

  // Finalizar partida e mostrar estatísticas
  const finalizarPartida = async () => {
    if (!partidaId) return;

    setLoading(true);

    try {
      const response = await api.post(`/sophygames/partida/finalizar/${partidaId}`);

      if (response.data.sucesso) {
        const stats = response.data.dados;
        
        alert(`🎉 Partida Finalizada!\n\n` +
          `✅ Acertos: ${stats.respostas_corretas}/${stats.total_respostas}\n` +
          `⏱️ Tempo Total: ${Math.floor(stats.tempo_total_segundos / 60)}m ${stats.tempo_total_segundos % 60}s\n` +
          `🏆 Pontuação: ${stats.pontuacao} pontos\n` +
          `📊 Taxa de Acerto: ${stats.taxa_acerto.toFixed(1)}%`
        );

        reiniciarJogo();
      } else {
        alert('Erro ao finalizar partida: ' + response.data.mensagem);
      }
    } catch (error: any) {
      console.error('[SOPHYGAMES] Erro ao finalizar partida:', error);
      alert('Erro ao finalizar partida');
    } finally {
      setLoading(false);
    }
  };

  // Formatar tempo em MM:SS
  const formatarTempo = (segundos: number) => {
    const mins = Math.floor(segundos / 60);
    const secs = segundos % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="container-fluid">
      <div className="row mb-4">
        <div className="col-md-8">
          <h2 className="mb-2">
            <i className="fas fa-gamepad mr-2"></i>
            🎮 SophyGames - Flashcards
          </h2>
          <p className="text-muted">
            Aprenda termos de Psicologia de forma divertida e interativa
          </p>
        </div>
        <div className="col-md-4 text-right">
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() => navigate('/dashboard')}
          >
            <i className="fas fa-arrow-left mr-2"></i>
            Voltar ao Dashboard
          </button>
        </div>
      </div>

      {/* Navegação rápida entre jogos em mobile */}
      <div className="d-md-none mb-3 mobile-games-nav">
        <div className="btn-group w-100" role="group" aria-label="Selecionar jogo">
          <button
            type="button"
            className={`btn btn-sm ${location.pathname === '/sophygames' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => navigate('/sophygames')}
          >
            <i className="fas fa-clone mr-1"></i>
            Flashcards
          </button>
          <button
            type="button"
            className={`btn btn-sm ${location.pathname === '/sophygames/cacapalavras' ? 'btn-success' : 'btn-outline-success'}`}
            onClick={() => navigate('/sophygames/cacapalavras')}
          >
            <i className="fas fa-search mr-1"></i>
            Caça-Palavras
          </button>
          <button
            type="button"
            className={`btn btn-sm ${location.pathname === '/sophygames/palavrascruzadas' ? 'btn-warning' : 'btn-outline-warning'}`}
            onClick={() => navigate('/sophygames/palavrascruzadas')}
          >
            <i className="fas fa-puzzle-piece mr-1"></i>
            Cruzadas
          </button>
        </div>
      </div>
      {jogoIniciado && modo === 'competicao' && respostas.length > 0 && (
        <div className="alert alert-info d-md-none py-1 mb-3 text-center">
          <small>{respostas.length} resposta(s) registradas</small>
        </div>
      )}

      <div className="row">
        <div className="col-12">
          {!jogoIniciado ? (
            // ========================================
            // Tela de Configuração
            // ========================================
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">📚 Configurar Jogo</h3>
              </div>
              <div className="card-body">
                <div className="row">
                  {/* Modo de Jogo */}
                  <div className="col-md-12 mb-4">
                    <div className="form-label fw-bold">🎯 Modo de Jogo</div>
                    <div className="btn-group d-block" role="group">
                      <button
                        type="button"
                        className={`btn ${modo === 'estudo' ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => setModo('estudo')}
                      >
                        📖 Modo Estudo (Visualizar)
                      </button>
                      <button
                        type="button"
                        className={`btn ${modo === 'competicao' ? 'btn-success' : 'btn-outline-success'}`}
                        onClick={() => setModo('competicao')}
                      >
                        🏆 Modo Competição (Responder + Timer)
                      </button>
                    </div>
                    <small className="form-text text-muted">
                      {modo === 'estudo' 
                        ? 'Estude os termos sem pressão de tempo' 
                        : 'Teste seus conhecimentos com timer e pontuação!'}
                    </small>
                  </div>

                  {/* Tema */}
                  <div className="col-md-6 mb-3">
                    <label className="form-label" htmlFor="sophyGamesTema">🎨 Tema (Opcional)</label>
                    <select 
                      id="sophyGamesTema"
                      className="form-select"
                      value={tema}
                      onChange={(e) => setTema(e.target.value)}
                    >
                      <option value="">Todos os temas</option>
                      {temasAreas.temas.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>

                  {/* Área de Estudo */}
                  <div className="col-md-6 mb-3">
                    <label className="form-label" htmlFor="sophyGamesAreaEstudo">📖 Área de Estudo (Opcional)</label>
                    <select 
                      id="sophyGamesAreaEstudo"
                      className="form-select"
                      value={areaEstudo}
                      onChange={(e) => setAreaEstudo(e.target.value)}
                    >
                      <option value="">Todas as áreas</option>
                      {temasAreas.areas.map((a) => (
                        <option key={a} value={a}>{a}</option>
                      ))}
                    </select>
                  </div>

                  {/* Dificuldade Mínima */}
                  <div className="col-md-4 mb-3">
                    <label className="form-label" htmlFor="sophyGamesDificuldadeMin">⭐ Dificuldade Mínima</label>
                    <input
                      id="sophyGamesDificuldadeMin"
                      type="number"
                      className="form-control"
                      min="1"
                      max="10"
                      value={dificuldadeMin}
                      onChange={(e) => setDificuldadeMin(parseInt(e.target.value))}
                    />
                  </div>

                  {/* Dificuldade Máxima */}
                  <div className="col-md-4 mb-3">
                    <label className="form-label" htmlFor="sophyGamesDificuldadeMax">⭐ Dificuldade Máxima</label>
                    <input
                      id="sophyGamesDificuldadeMax"
                      type="number"
                      className="form-control"
                      min="1"
                      max="10"
                      value={dificuldadeMax}
                      onChange={(e) => setDificuldadeMax(parseInt(e.target.value))}
                    />
                  </div>

                  {/* Total de Cartas */}
                  <div className="col-md-4 mb-3">
                    <label className="form-label" htmlFor="sophyGamesTotalCartas">🃏 Total de Cartas</label>
                    <input
                      id="sophyGamesTotalCartas"
                      type="number"
                      className="form-control"
                      min="5"
                      max="50"
                      value={totalCartas}
                      onChange={(e) => setTotalCartas(parseInt(e.target.value))}
                    />
                  </div>
                </div>

                <div className="text-center mt-4">
                  <button
                    className="btn btn-primary btn-lg"
                    onClick={iniciarJogo}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" />
                        Carregando...
                      </>
                    ) : (
                      <>🚀 Iniciar Jogo</>
                    )}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            // ========================================
            // Tela de Jogo
            // ========================================
            <>
              {/* Progresso e Timer */}
              <div className="alert alert-info d-flex justify-content-between align-items-center">
                <div>
                  <strong>Carta {indiceAtual + 1} de {termos.length}</strong>
                </div>
                {modo === 'competicao' && (
                  <div className="text-center">
                    <strong className="text-primary" style={{ fontSize: '1.5rem' }}>
                      ⏱️ {formatarTempo(timer)}
                    </strong>
                  </div>
                )}
                <div>
                  <button className="btn btn-sm btn-danger" onClick={reiniciarJogo}>
                    🔄 Reiniciar
                  </button>
                </div>
              </div>

              {/* MODO ESTUDO */}
              {modo === 'estudo' && termoAtual && (
                <>
                  {/* Flashcard */}
                  <Flashcard
                    termo={termoAtual.termo}
                    significado={termoAtual.significado}
                    descricao={termoAtual.descricao}
                    dificuldade={termoAtual.dificuldade}
                  />

                  {/* Navegação */}
                  <div className="mt-4 d-flex justify-content-center gap-3">
                    <button
                      className="btn btn-secondary btn-lg"
                      onClick={cartaAnterior}
                      disabled={indiceAtual === 0}
                    >
                      ⬅️ Anterior
                    </button>
                    <button
                      className="btn btn-primary btn-lg"
                      onClick={proximoCarta}
                    >
                      {indiceAtual === termos.length - 1 ? '✅ Finalizar' : 'Próximo ➡️'}
                    </button>
                  </div>
                </>
              )}

              {/* MODO COMPETIÇÃO */}
              {modo === 'competicao' && termoAtual && (
                <div className="row justify-content-center" style={{ padding: '0 20px' }}>
                  <div className="col-md-10 col-lg-8">
                    <div className="card shadow-lg">
                      <div className="card-body p-5">
                        {/* Termo e Dificuldade */}
                        <div className="text-center mb-4">
                          <div className="d-flex justify-content-center align-items-center gap-3 mb-3">
                            <h2 className="mb-0">{termoAtual.termo}</h2>
                            <button
                              className="btn btn-sm btn-info"
                              onClick={() => setMostrarDescricao(!mostrarDescricao)}
                              title="Ver descrição completa"
                            >
                              ℹ️
                            </button>
                          </div>
                          <div>
                            <span className="badge bg-primary">
                              {'★'.repeat(Math.min(termoAtual.dificuldade, 10))} {termoAtual.dificuldade}/10
                            </span>
                          </div>
                        </div>

                        {/* Descrição Completa (toggle) */}
                        {mostrarDescricao && termoAtual.descricao && (
                          <div className="alert alert-info mb-4">
                            <strong>ℹ️ Descrição:</strong>
                            <p className="mb-0 mt-2">{termoAtual.descricao}</p>
                          </div>
                        )}

                        {/* Resultado da Resposta Anterior */}
                        {resultadoResposta && (
                          <div className={`alert ${resultadoResposta.correta ? 'alert-success' : 'alert-danger'} mb-4`}>
                            <h5>{resultadoResposta.correta ? '✅ Correto!' : '❌ Incorreto'}</h5>
                            <p className="mb-0">
                              <strong>Significado correto:</strong> {resultadoResposta.significadoCorreto}
                            </p>
                          </div>
                        )}

                        {/* Campo de Resposta */}
                        {!resultadoResposta && (
                          <>
                            <div className="mb-4">
                              <label className="form-label" htmlFor="sophyGamesResposta">
                                <strong>💭 Digite o significado do termo:</strong>
                              </label>
                              <textarea
                                id="sophyGamesResposta"
                                className="form-control"
                                rows={4}
                                placeholder="Ex: Parte inconsciente da personalidade..."
                                value={respostaUsuario}
                                onChange={(e) => setRespostaUsuario(e.target.value)}
                                disabled={loading}
                                autoFocus
                              />
                            </div>

                            {/* Botão Enviar Resposta */}
                            <div className="text-center">
                              <button
                                className="btn btn-success btn-lg px-5"
                                onClick={enviarResposta}
                                disabled={loading || !respostaUsuario.trim()}
                              >
                                {loading ? (
                                  <>
                                    <span className="spinner-border spinner-border-sm me-2" />
                                    Enviando...
                                  </>
                                ) : (
                                  <>📤 Enviar Resposta</>
                                )}
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
